import React, { useState } from "react";

import Button from "../../../components/ui/Button";
import Card from "../../../components/ui/Card";
import CreatePostModal from "../components/CreatePostModal";
import BoostPostModal from "../components/modals/BoostPostModal";
import BoostInsightsModal from "../components/modals/BoostInsightsModal";
import { PlusCircle, Edit, Trash2, TrendingUp, MessageSquare, Eye, Heart, MoreVertical, ShoppingBag } from "lucide-react";
import { Menu } from "@headlessui/react";
import Tabs from "../../../components/ui/Tabs";

const ContentHubPage = () => {
  // Initialize with mock posts that have stats
  const [posts, setPosts] = useState([
    {
      id: "post_1",
      title: "Concert Announcement",
      content: "Excited to announce my upcoming concert in Addis Ababa! 🎤",
      imageUrl: "https://placehold.co/400x300/7C3AED/FFFFFF?text=Concert+Announcement",
      status: "Published",
      publishDate: "2 hours ago",
      createdAt: "2 hours ago",
      likes: 234,
      comments: 45,
      views: 1543,
      boosted: false,
      taggedProducts: []
    },
    {
      id: "post_2",
      title: "New Track Release",
      content: "Just released a new track! Check it out on all platforms 🎵",
      status: "Published",
      publishDate: "1 day ago",
      createdAt: "1 day ago",
      likes: 567,
      comments: 89,
      views: 3421,
      boosted: true,
      boostData: {
        budget: 5000,
        duration: 7,
        estimatedReach: 45000,
        startDate: "Nov 28, 2025",
        placement: "feed"
      },
      taggedProducts: [
        { id: "prod_1", name: "Official T-Shirt", priceETB: 350 }
      ]
    },
    {
      id: "post_3",
      title: "Behind the Scenes",
      content: "Behind the scenes from my latest music video shoot 📸",
      status: "Draft",
      publishDate: "-",
      createdAt: "3 days ago",
      likes: 0,
      comments: 0,
      views: 0,
      boosted: false,
      taggedProducts: []
    },
    {
      id: "post_4",
      title: "Upcoming Q&A",
      content: "Don't miss the live Q&A session this Friday! 💬",
      status: "Scheduled",
      publishDate: "in 2 days",
      createdAt: "1 hour ago",
      likes: 0,
      comments: 0,
      views: 0,
      boosted: false,
      taggedProducts: []
    }
  ]);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isBoostModalOpen, setIsBoostModalOpen] = useState(false);
  const [isInsightsModalOpen, setIsInsightsModalOpen] = useState(false);
  const [postToEdit, setPostToEdit] = useState(null);
  const [postToBoost, setPostToBoost] = useState(null);
  const [postForInsights, setPostForInsights] = useState(null);

  const handleSavePost = (postData) => {
    const isEditing = posts.some((p) => p.id === postData.id);
    if (isEditing) {
      setPosts(posts.map((p) => (p.id === postData.id ? { ...p, ...postData } : p)));
    } else {
      const newPost = {
        ...postData,
        id: `post_${Date.now()}`,
        status: "Published",
        publishDate: "Just now",
        createdAt: "Just now",
        likes: 0,
        comments: 0,
        views: 0,
        boosted: false
      };
      setPosts([newPost, ...posts]);
    }
    setIsCreateModalOpen(false);
    setPostToEdit(null);
  };

  const handleDelete = (postId) => {
    if (window.confirm("Are you sure you want to delete this post?")) {
      setPosts(posts.filter(p => p.id !== postId));
    }
  };

  const handleBoostClick = (post) => {
    setPostToBoost(post);
    setIsBoostModalOpen(true);
  };

  const handleInsightsClick = (post) => {
    setPostForInsights(post);
    setIsInsightsModalOpen(true);
  };

  const handleBoostConfirm = (boostData) => {
    setPosts(posts.map(p => 
      p.id === boostData.postId ? { 
        ...p, 
        boosted: true, 
        boostData: { ...boostData, startDate: new Date().toLocaleDateString() } 
      } : p
    ));
    setIsBoostModalOpen(false); // Ensure modal closes
  };

  const openCreateModal = () => {
    setPostToEdit(null);
    setIsCreateModalOpen(true);
  };

  const openEditModal = (post) => {
    setPostToEdit(post);
    setIsCreateModalOpen(true);
  };

  return (
    <>
      <div className="max-w-4xl mx-auto">
        <div className="flex flex-col @md:flex-row items-start @md:items-center justify-between mb-8 gap-4">
          <div>
            <h1 className="text-2xl @sm:text-3xl font-bold text-slate-800">Content Hub</h1>
            <p className="text-slate-500 mt-1 text-sm">
              Manage, edit, and boost your published content.
            </p>
          </div>
          <Button variant="primary" onClick={openCreateModal} className="w-full @md:w-auto">
            <PlusCircle size={18} className="mr-2" />
            Create New Post
          </Button>
        </div>

        <div className="space-y-6">
          <Tabs tabs={[
            {
              name: "All Posts",
              content: <PostList posts={posts} isScheduled={false} />
            },
            {
              name: "Published",
              content: <PostList posts={posts.filter(p => p.status === 'Published')} isScheduled={false} />
            },
            {
              name: "Scheduled",
              content: <PostList posts={posts.filter(p => p.status === 'Scheduled')} isScheduled={true} />
            },
            {
              name: "Drafts",
              content: <PostList posts={posts.filter(p => p.status === 'Draft')} isScheduled={false} />
            }
          ]} />
        </div>
      </div>

      <CreatePostModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSave={handleSavePost}
        postToEdit={postToEdit}
      />

      <BoostPostModal
        isOpen={isBoostModalOpen}
        onClose={() => setIsBoostModalOpen(false)}
        post={postToBoost}
        onConfirm={handleBoostConfirm}
      />

      <BoostInsightsModal
        isOpen={isInsightsModalOpen}
        onClose={() => setIsInsightsModalOpen(false)}
        post={postForInsights}
      />
    </>
  );

  function PostList({ posts, isScheduled }) {
    return (
        <div className="space-y-6 mt-6">
          {posts.map(post => (
            <Card key={post.id} className="overflow-hidden !p-0">
              <div className="p-4 @sm:p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        post.status === 'Published' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {post.status}
                      </span>
                      <span className="text-xs text-slate-400">{post.publishDate}</span>
                    </div>
                    <p className="text-slate-800 text-lg font-medium mb-1">{post.title}</p>
                    <p className="text-slate-600">{post.content}</p>
                    
                    {/* Tagged Products Display */}
                    {post.taggedProducts && post.taggedProducts.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {post.taggedProducts.map(prod => (
                          <div key={prod.id} className="inline-flex items-center bg-slate-100 text-slate-700 px-2 py-1 rounded-md text-xs border border-slate-200">
                            <ShoppingBag size={12} className="mr-1 text-slate-500" />
                            {prod.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  <Menu as="div" className="relative ml-3">
                    <Menu.Button className="p-2 hover:bg-slate-100 rounded-lg transition">
                      <MoreVertical size={20} className="text-slate-500" />
                    </Menu.Button>
                    <Menu.Items className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 z-10 py-1">
                      <Menu.Item>
                        <button
                          onClick={() => openEditModal(post)}
                          className="w-full flex items-center px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                        >
                          <Edit size={16} className="mr-2" />
                          Edit Post
                        </button>
                      </Menu.Item>
                      <Menu.Item>
                        <button
                          onClick={() => handleBoostClick(post)}
                          className="w-full flex items-center px-4 py-2 text-sm text-primary hover:bg-purple-50"
                        >
                          <TrendingUp size={16} className="mr-2" />
                          {post.boosted ? 'Boost Settings' : 'Boost Post'}
                        </button>
                      </Menu.Item>
                      <Menu.Item>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={16} className="mr-2" />
                          Delete Post
                        </button>
                      </Menu.Item>
                    </Menu.Items>
                  </Menu>
                </div>

                {post.imageUrl && (
                  <img 
                    src={post.imageUrl} 
                    alt="Post" 
                    className="w-full h-64 object-cover rounded-lg mb-4"
                  />
                )}

                {post.boosted && (
                  <div className="flex items-center justify-between mb-4 px-4 py-3 bg-purple-50 rounded-lg border border-purple-100">
                    <div className="flex items-center space-x-2">
                      <TrendingUp size={18} className="text-purple-600" />
                      <div>
                        <p className="text-sm font-bold text-purple-900">Post is Boosted</p>
                        <p className="text-xs text-purple-700">Active until {new Date(Date.now() + 86400000 * 3).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="bg-white border-purple-200 text-purple-700 hover:bg-purple-100"
                      onClick={() => handleInsightsClick(post)}
                    >
                      View Insights
                    </Button>
                  </div>
                )}

                <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                  <div className="flex space-x-6 text-slate-500">
                    <div className="flex items-center space-x-2">
                      <Heart size={18} />
                      <span className="text-sm font-medium">{post.likes}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <MessageSquare size={18} />
                      <span className="text-sm font-medium">{post.comments}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Eye size={18} />
                      <span className="text-sm font-medium">{post.views}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          ))}

          {posts.length === 0 && (
            <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
              <PlusCircle size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900">No posts yet</h3>
              <p className="text-slate-500 mb-4">Create your first post to start engaging with your audience.</p>
              <Button variant="primary" onClick={openCreateModal}>Create Post</Button>
            </div>
          )}
        </div>
    );
  }
};

export default ContentHubPage;
